import { useLayoutEffect, type RefObject } from 'react'
import type {
  CellKeyDownEvent,
  FullWidthCellKeyDownEvent,
} from 'ag-grid-community'
import { toast } from '@/stores/useToastStore'
import { validateWavePercent } from './model'

/**
 * Opens an AG Grid cell editor the way a spreadsheet does: a typed character
 * replaces the value (AG Grid starts editing on that key without delivering
 * it to the input), Backspace/Delete clear it, Enter/F2/double-click select it.
 * Runs before paint, so keys typed right after the first one reach the input.
 */
export function useSpreadsheetStart(
  eventKey: string | null,
  onValueChange: (value: string) => void,
  input: RefObject<HTMLInputElement | null>
) {
  useLayoutEffect(() => {
    const element = input.current
    const seed =
      eventKey === 'Backspace' || eventKey === 'Delete'
        ? ''
        : eventKey?.length === 1
          ? eventKey
          : null
    if (seed !== null) {
      onValueChange(seed)
      // Shown at once: a key typed before the next render appends to it.
      if (element) element.value = seed
    }
    element?.focus()
    if (seed === null) element?.select()
    // Only when the editor opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

/**
 * Whether a percent editor closing with ``value`` (e.g. on a click away) must
 * drop it as invalid; says so, rather than reverting silently.
 */
export function dropInvalidPercent(value: string): boolean {
  const error = validateWavePercent(value)
  if (error) toast.warning(`Not saved. ${error}`)
  return error !== null
}

/**
 * Inside a cell, let the browser handle Tab/Enter/Space on a focused link or
 * button, so the controls of a cell are reachable and usable by keyboard.
 */
export function keepNativeKeys(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null
  return (
    !!target?.closest('a, button') && ['Tab', 'Enter', ' '].includes(event.key)
  )
}

/** Enter on a focused cell moves focus to its first link or button. */
export function focusCellControl<TData>(
  event: CellKeyDownEvent<TData> | FullWidthCellKeyDownEvent<TData>
) {
  const keyboard = event.event as KeyboardEvent | null
  if (keyboard?.key !== 'Enter') return
  const cell = (keyboard.target as HTMLElement | null)?.closest('.ag-cell')
  const control = cell?.querySelector<HTMLElement>(
    'a[href], button:not(:disabled)'
  )
  if (control && keyboard.target === cell) {
    keyboard.preventDefault()
    control.focus()
  }
}
